// Canvas v2 chrome overlay. Lives inside CanvasEditor (`position:fixed,
// inset:0, pointerEvents:none, zIndex:100`). Every layer here is in
// SCREEN-SPACE pixels — they do not zoom with the canvas. State lives
// in v2 reactive store; positions update imperatively from
// state.js / pointer.js / selection.js so 60fps gestures don't trigger
// re-renders.
//
// Layer keys use a `Cv` prefix so PascalCase auto-extend doesn't
// accidentally pull in shared/preview/brand components.
//
// Layer stack (bottom → top):
//   CvConnectors   — future: arrows between artboards (SVG)
//   CvGuidesLayer  — user-drawn ruler guides
//   CvSnapLines    — fixed pool of 4 snap-feedback lines
//   CvMarquee      — selection rect during marquee drag
//   CvSelectionBox — outline around selection bbox
//   CvHandle*      — 8 resize handles
//   CvSelectedHost — wraps shared SelectedNavbar above selection
//   CvRulerTop/Left/Corner — drag-to-spawn guides
//   CvModesBar     — V/H/I mode toolbar
//   CvCanvasSettingsModal — opens from chrome trigger; toggles snap/grid

// Inset so screen-space chrome (rulers, drag marquee) doesn't visually
// overlap the fixed CanvasAppHeader (top) or floating WorkspaceNavbar
// (left). Use DS spacing token `D` (67px) — clears the 56px header and
// the 36px-wide left navbar with ~8px breathing room.
const NAV_INSET = 'D'
// Ruler corner / strip thickness — DS token `A` (16px). Used for both
// the corner square and the ruler strip width.
const RULER_THICKNESS = 'A'
// Plain CSS-variable forms for use inside CSS function args (clip-path,
// calc) where the DOMQL token resolver doesn't reach.
const NAV_INSET_VAR = 'var(--spacing-d)'
const RULER_THICKNESS_VAR = 'var(--spacing-a)'

const handleBase = {
  tag: 'div',
  position: 'absolute',
  width: '14px',
  height: '14px',
  background: 'rgba(64,156,255,1)',
  pointerEvents: 'auto',
  touchAction: 'none',
  boxSizing: 'border-box'
}

const handle = (handleKey, cursor) => ({
  ...handleBase,
  attr: { 'data-handle': handleKey, 'aria-label': `Resize ${handleKey.toUpperCase()}` },
  style: {
    display: 'none',
    border: '2px solid white',
    borderRadius: '3px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.4)',
    transform: 'translate(-50%, -50%)',
    transition: 'transform 80ms ease, box-shadow 80ms ease',
    cursor,
    ':hover': { transform: 'translate(-50%, -50%) scale(1.25)', boxShadow: '0 2px 6px rgba(0,0,0,0.5)' }
  },
  onPointerDown: (ev, el) => {
    ev.stopPropagation()
    const v2 = el.call('getCanvasV2')
    const key = v2?.selection?.primary
    if (!key) return
    el.call('beginResize', ev, key, handleKey)
  },
  onPointerMove: (ev, el) => el.call('trackPointerMove', ev),
  onPointerUp: (ev, el) => el.call('trackPointerEnd', ev),
  onPointerCancel: (ev, el) => el.call('trackPointerEnd', ev)
})

const ruler = (axis) => ({
  tag: 'div',
  position: 'absolute',
  pointerEvents: 'auto',
  touchAction: 'none',
  background: 'rgba(0,0,0,0.6)',
  style: {
    cursor: axis === 'h' ? 'ns-resize' : 'ew-resize',
    backgroundImage: axis === 'h'
      ? 'repeating-linear-gradient(to right, rgba(255,255,255,0.25) 0 1px, transparent 1px 50px)'
      : 'repeating-linear-gradient(to bottom, rgba(255,255,255,0.25) 0 1px, transparent 1px 50px)',
    [axis === 'h' ? 'borderBottom' : 'borderRight']: '1px solid rgba(255,255,255,0.15)'
  },
  onPointerDown: (ev, el) => el.call('spawnGuide', axis, ev),
  onPointerMove: (ev, el) => el.call('trackPointerMove', ev),
  onPointerUp: (ev, el) => el.call('trackPointerEnd', ev),
  onPointerCancel: (ev, el) => el.call('trackPointerEnd', ev)
})

const modeBtn = (mode, label, key) => ({
  tag: 'button',
  type: 'button',
  text: `${label}  ${key.toUpperCase()}`,
  attr: { 'data-mode': mode, title: `${label} (${key.toUpperCase()})`, 'aria-label': label },
  background: 'transparent',
  border: '0',
  color: 'white',
  fontSize: 'Y',
  padding: 'W A',
  round: 'Y',
  cursor: 'pointer',
  pointerEvents: 'auto',
  isActive: (el) => el.call('getCanvasMode') === mode,
  '.isActive': { background: 'rgba(64,156,255,0.4)' },
  onClick: (ev, el) => { ev.stopPropagation(); el.call('setCanvasV2Mode', mode) }
})

const settingsRow = (label, control) => ({
  tag: 'label',
  display: 'flex',
  flow: 'x',
  align: 'center space-between',
  gap: 'A',
  padding: 'W 0',
  fontSize: 'Y',
  CvLabel: { tag: 'span', text: label, color: 'white', opacity: '.85' },
  CvControl: control
})

export const CanvasOverlayV2 = {
  tag: 'div',
  position: 'absolute',
  inset: '0',
  pointerEvents: 'none',
  if: (el) => el.call('isCanvasV2'),

  onRender: (el) => {
    if (el.__cvChromeInit) return
    el.__cvChromeInit = true
    el.call('refreshCanvasChrome')
  },

  CvConnectors: {
    tag: 'svg',
    position: 'absolute',
    inset: '0',
    pointerEvents: 'none',
    style: { display: 'block', width: '100%', height: '100%' }
  },

  CvGuidesLayer: {
    tag: 'div',
    position: 'absolute',
    inset: '0',
    pointerEvents: 'none',
    children: (el) => {
      const v2 = el.call('getCanvasV2')
      if (!v2) return []
      const h = (v2.guides.horizontal || []).map((pos, i) => ({ axis: 'h', pos, index: i }))
      const v = (v2.guides.vertical || []).map((pos, i) => ({ axis: 'v', pos, index: i }))
      return [...h, ...v]
    },
    childrenAs: 'state',
    childExtends: 'CanvasGuide'
  },

  CvSnapLines: {
    tag: 'div',
    position: 'absolute',
    inset: '0',
    pointerEvents: 'none',
    Line1: { tag: 'div', position: 'absolute', background: 'rgba(64,156,255,1)', style: { display: 'none' } },
    Line2: { tag: 'div', position: 'absolute', background: 'rgba(64,156,255,1)', style: { display: 'none' } },
    Line3: { tag: 'div', position: 'absolute', background: 'rgba(64,156,255,1)', style: { display: 'none' } },
    Line4: { tag: 'div', position: 'absolute', background: 'rgba(64,156,255,1)', style: { display: 'none' } }
  },

  // Viewport-sized clip wrapper so the drag-to-select marquee is
  // visually clipped to the non-nav region. CvMarquee's imperative
  // `left/top` remain in screen coords; the clip-path on this parent
  // hides whatever bleeds into the top header or left floating navbar.
  CvDragAreaClip: {
    tag: 'div',
    position: 'absolute',
    inset: '0',
    pointerEvents: 'none',
    style: { clipPath: `inset(${NAV_INSET_VAR} 0 0 ${NAV_INSET_VAR})` },
    CvMarquee: {
      tag: 'div',
      position: 'absolute',
      pointerEvents: 'none',
      background: 'rgba(64,156,255,0.12)',
      style: {
        outline: '1px solid rgba(64,156,255,0.9)',
        display: 'none', left: '0', top: '0', width: '0', height: '0'
      }
    }
  },

  // Group bounding boxes — dashed outlines drawn behind selection. One
  // div per group, populated imperatively by refreshCanvasChrome.
  CvGroupsLayer: {
    tag: 'div',
    position: 'absolute',
    inset: '0',
    pointerEvents: 'none'
  },

  CvSelectionBox: {
    tag: 'div',
    position: 'absolute',
    pointerEvents: 'auto',
    touchAction: 'none',
    boxSizing: 'border-box',
    style: {
      display: 'none',
      outline: '2px solid rgba(64,156,255,1)',
      outlineOffset: '0px',
      background: 'rgba(64,156,255,0.04)',
      boxShadow: '0 0 0 1px rgba(255,255,255,0.15) inset',
      cursor: 'move',
      left: '0', top: '0', width: '0', height: '0'
    },
    onPointerDown: (ev, el) => {
      ev.stopPropagation()
      const v2 = el.call('getCanvasV2')
      const primary = v2?.selection?.primary
      if (primary) el.call('beginDrag', ev, primary)
    },
    onPointerMove: (ev, el) => el.call('trackPointerMove', ev),
    onPointerUp: (ev, el) => el.call('trackPointerEnd', ev),
    onPointerCancel: (ev, el) => el.call('trackPointerEnd', ev)
  },

  CvHandleNW: handle('nw', 'nwse-resize'),
  CvHandleN:  handle('n',  'ns-resize'),
  CvHandleNE: handle('ne', 'nesw-resize'),
  CvHandleE:  handle('e',  'ew-resize'),
  CvHandleSE: handle('se', 'nwse-resize'),
  CvHandleS:  handle('s',  'ns-resize'),
  CvHandleSW: handle('sw', 'nesw-resize'),
  CvHandleW:  handle('w',  'ew-resize'),

  // Per-item titles in screen-space — populated imperatively by
  // refreshCanvasChrome. Each title sits just above its item.
  CvItemTitles: {
    tag: 'div',
    position: 'absolute',
    inset: '0',
    pointerEvents: 'none'
  },

  CvSelectedHost: {
    tag: 'div',
    position: 'absolute',
    pointerEvents: 'none',
    style: { display: 'none', left: '0', top: '0', width: '0', height: '0' },
    SelectedNavbar: { pointerEvents: 'auto' }
  },

  CvRulerCorner: {
    tag: 'div',
    position: 'absolute',
    top: NAV_INSET,
    left: NAV_INSET,
    width: RULER_THICKNESS,
    height: RULER_THICKNESS,
    pointerEvents: 'none',
    background: 'rgba(0,0,0,0.7)',
    show: (el) => !!el.call('getCanvasV2')?.showRulers,
    style: {
      borderRight: '1px solid rgba(255,255,255,0.15)',
      borderBottom: '1px solid rgba(255,255,255,0.15)'
    }
  },
  CvRulerTop: { ...ruler('h'), top: NAV_INSET, left: `calc(${NAV_INSET_VAR} + ${RULER_THICKNESS_VAR})`, right: '0', height: RULER_THICKNESS,
    show: (el) => !!el.call('getCanvasV2')?.showRulers },
  CvRulerLeft: { ...ruler('v'), top: `calc(${NAV_INSET_VAR} + ${RULER_THICKNESS_VAR})`, left: NAV_INSET, bottom: '0', width: RULER_THICKNESS,
    show: (el) => !!el.call('getCanvasV2')?.showRulers },

  CvModesBar: {
    tag: 'div',
    position: 'absolute',
    bottom: 'X2',
    left: 'X2',
    pointerEvents: 'auto',
    display: 'flex',
    flow: 'x',
    gap: 'W',
    padding: 'W',
    round: 'Z',
    background: 'rgba(0,0,0,0.7)',
    style: { backdropFilter: 'blur(8px)' },
    CvModeMove:     modeBtn('default',  '✥ Move',     'v'),
    CvModePan:      modeBtn('pan',      '✋ Pan',      'h'),
    CvModeInteract: modeBtn('interact', '☞ Interact', 'u'),
    CvModeInspect:  modeBtn('inspect',  '⌖ Inspect',  'i')
  },

  // Replaced by the unified `SettingsModal` mounted from CanvasEditor.
  // Kept as `null` so the legacy key in the parent tree explicitly opts
  // out of re-creating the old v2-scoped modal.
  CvCanvasSettingsModal: null,

  // Right-click context menu — positioned imperatively at click point.
  CvContextMenu: {
    tag: 'div',
    position: 'absolute',
    pointerEvents: 'auto',
    background: '#1c1f24',
    color: 'white',
    round: 'W',
    padding: 'W',
    fontSize: 'Y',
    style: {
      display: 'none',
      left: '0', top: '0', minWidth: '180px',
      boxShadow: '0 8px 24px rgba(0,0,0,0.6)',
      border: '1px solid rgba(255,255,255,0.1)',
      zIndex: '210'
    },
    onPointerDown: (ev) => ev.stopPropagation(),

    Duplicate: { tag: 'div', text: 'Duplicate  ⌘D', padding: 'W A', cursor: 'pointer', round: 'W',
      style: { ':hover': { background: 'rgba(255,255,255,0.08)' } },
      onClick: (ev, el) => { ev.stopPropagation(); el.call('duplicateSelection'); el.call('hideContextMenu') } },
    Delete:    { tag: 'div', text: 'Delete  ⌫', padding: 'W A', cursor: 'pointer', round: 'W',
      style: { ':hover': { background: 'rgba(255,255,255,0.08)' } },
      onClick: (ev, el) => { ev.stopPropagation(); el.call('deleteSelection'); el.call('hideContextMenu') } },
    GroupBtn:  { tag: 'div', text: 'Group  ⌘G', padding: 'W A', cursor: 'pointer', round: 'W',
      style: { ':hover': { background: 'rgba(255,255,255,0.08)' } },
      onClick: (ev, el) => { ev.stopPropagation(); el.call('group'); el.call('hideContextMenu') } },
    UngroupBtn: { tag: 'div', text: 'Ungroup  ⌘⇧G', padding: 'W A', cursor: 'pointer', round: 'W',
      style: { ':hover': { background: 'rgba(255,255,255,0.08)' } },
      onClick: (ev, el) => {
        ev.stopPropagation()
        const v2 = el.call('getCanvasV2')
        const ids = v2?.selection?.ids || []
        Object.entries(v2?.groups || {})
          .filter(([, g]) => g.children.some((c) => ids.includes(c)))
          .forEach(([gid]) => el.call('ungroup', gid))
        el.call('hideContextMenu')
      } },
    Sep1: { tag: 'div', height: '1px', background: 'rgba(255,255,255,0.1)', margin: 'W 0' },
    SelectAll: { tag: 'div', text: 'Select all  ⌘A', padding: 'W A', cursor: 'pointer', round: 'W',
      style: { ':hover': { background: 'rgba(255,255,255,0.08)' } },
      onClick: (ev, el) => {
        ev.stopPropagation()
        el.call('selectMany', Object.keys(el.call('getCanvasV2')?.items || {}))
        el.call('hideContextMenu')
      } },
    AutoLayout: { tag: 'div', text: 'Auto-layout  ⌘L', padding: 'W A', cursor: 'pointer', round: 'W',
      style: { ':hover': { background: 'rgba(255,255,255,0.08)' } },
      onClick: (ev, el) => { ev.stopPropagation(); el.call('autoLayout'); el.call('hideContextMenu') } },
    ResetView: { tag: 'div', text: 'Reset view  ⌘0', padding: 'W A', cursor: 'pointer', round: 'W',
      style: { ':hover': { background: 'rgba(255,255,255,0.08)' } },
      onClick: (ev, el) => { ev.stopPropagation(); el.call('resetCanvasView'); el.call('hideContextMenu') } }
  },

  // Minimap — small bottom-left overview of all items + viewport rect.
  // Rendered imperatively by refreshCanvasChrome.
  CvMinimap: {
    tag: 'div',
    position: 'absolute',
    bottom: '12px',
    left: '12px',
    width: '180px',
    height: '120px',
    pointerEvents: 'auto',
    cursor: 'pointer',
    background: 'rgba(0,0,0,0.55)',
    show: (el) => !!el.call('getCanvasV2')?.showMinimap,
    style: {
      border: '1px solid rgba(255,255,255,0.15)',
      borderRadius: '4px',
      overflow: 'hidden'
    },
    onClick: (ev, el) => { ev.stopPropagation(); el.call('fitToSelection') }
  },

  CvShortcutsOverlay: {
    tag: 'div',
    position: 'absolute',
    inset: '0',
    pointerEvents: 'auto',
    background: 'rgba(0,0,0,0.6)',
    display: 'flex',
    align: 'center center',
    show: (el) => !!el.call('getCanvasV2')?.shortcutsOpen,
    style: { backdropFilter: 'blur(4px)', zIndex: '210' },
    onPointerDown: (ev, el) => { if (ev.target === el.node) el.call('toggleShortcutsOverlay') },
    Card: {
      tag: 'div',
      width: 'I',
      maxWidth: '92%',
      background: '#1c1f24',
      color: 'white',
      round: 'A',
      padding: 'B',
      style: { boxShadow: '0 30px 80px rgba(0,0,0,0.6)' },
      onPointerDown: (ev) => ev.stopPropagation(),
      Title: { tag: 'h3', text: 'Keyboard shortcuts', fontSize: 'A', margin: '0 0 A' },
      List: {
        display: 'grid',
        style: { gridTemplateColumns: 'auto 1fr', gap: '8px 24px', fontSize: '13px' },
        children: [
          ['V', 'Move tool'], ['H', 'Pan tool'], ['U', 'Interact tool'], ['I', 'Inspect tool'],
          ['Space (hold)', 'Temporary pan'],
          ['⌘A', 'Select all'], ['⌘D', 'Duplicate'], ['Delete', 'Delete'],
          ['⌘G', 'Group'], ['⌘⇧G', 'Ungroup'], ['⌘L', 'Auto-layout'],
          ['⌘Z', 'Undo'], ['⌘⇧Z', 'Redo'],
          ['Arrows', 'Nudge 1px'], ['Shift+Arrows', 'Nudge 10px'],
          ['⌘0', 'Reset view'], ['⌘1', 'Fit selection'],
          ['Ctrl+Wheel', 'Zoom around cursor'], ['Wheel', 'Pan'],
          ['⌘,', 'Settings'], ['⌘/', 'This dialog'], ['Esc', 'Clear / close']
        ].flatMap(([k, v]) => [
          { tag: 'kbd', text: k, padding: '2px 6px', round: 'W', background: 'rgba(255,255,255,0.1)', fontSize: '11px', style: { fontFamily: 'monospace' } },
          { tag: 'div', text: v, opacity: '.85' }
        ])
      },
      Close: {
        tag: 'button', type: 'button', text: 'Close', cursor: 'pointer', marginTop: 'A',
        background: 'rgba(255,255,255,0.08)', color: 'white',
        padding: 'W A', round: 'W', style: { border: '1px solid rgba(255,255,255,0.2)' },
        onClick: (ev, el) => { ev.stopPropagation(); el.call('toggleShortcutsOverlay') }
      }
    }
  },

  CvInspectOverlay: {
    tag: 'div',
    position: 'absolute',
    inset: '0',
    pointerEvents: 'none',
    show: (el) => el.call('getCanvasMode') === 'inspect',
    style: { boxShadow: 'inset 0 0 0 3px rgba(64,156,255,0.55)' }
  }
}
